import type { ComponentType } from "react";
import type {
  BuilderCampaignSnapshot,
  BuilderStepProps,
  ChannelConfig,
  LastActiveStepConfig,
  StepCompletion,
} from "campaign-builder/config/channelConfig";
import { registerChannelConfig } from "campaign-builder/config/channelRegistry";
import { getFormatsFor } from "campaign-builder/formats";
import { validateCampaignName } from "campaign-builder/state";

export const MIN_RECIPIENTS = 1000;

export interface AdmBuilderSnapshot extends BuilderCampaignSnapshot {
  audienceCount?: number;
  recipientsAmount?: number;
  deliveryRuleSelected?: boolean;
  returnAddressSelected?: boolean;
  hasDesign?: boolean;
  designHasAddressBox?: boolean;
  trackingValid?: boolean;
}

// Placeholder until the ADM targeting card is built.
const AdmTargetingStep: ComponentType<BuilderStepProps> = () => null;

const getAdmStepCompletion = (campaign: BuilderCampaignSnapshot): StepCompletion => {
  const c = campaign as AdmBuilderSnapshot;

  return {
    targeting:
      validateCampaignName(c.campaignName ?? "") === null &&
      !!c.startDate &&
      (c.audienceCount ?? 0) > 0 &&
      (c.recipientsAmount ?? 0) >= MIN_RECIPIENTS &&
      !!c.deliveryRuleSelected &&
      !!c.returnAddressSelected,
    design: !!c.hasDesign && !!c.designHasAddressBox,
    tracking: !!c.trackingValid,
  };
};

// Legacy ADM cursor vocab (home → segments → design → submit) persisted into
// `lastActiveStep` for the platform cards + Resume deep-link. "segments" = the
// targeting step is complete; "design" = design is complete; "submit" is written
// by the submit flow (not the write-through), so it's a terminal value here.
const admLastActiveStep: LastActiveStepConfig = {
  initial: "home",
  byCompletedStep: { targeting: "segments", design: "design" },
  terminalValues: ["submit"],
};

export const admChannelConfig: ChannelConfig = {
  channel: "ADM",
  label: "Addressed Direct Mail",
  TargetingStep: AdmTargetingStep,
  summaryUnits: {
    label: "Units",
    getValue: (campaign) => (campaign as AdmBuilderSnapshot).recipientsAmount ?? 0,
  },
  getFormats: (countryCode) => getFormatsFor("ADM", countryCode),
  design: {
    addressBoxRequired: true,
    maxFileSizeMb: 50,
    acceptedFileTypes: ["application/pdf"],
    showFormatSelector: true,
  },
  tracking: { promoCodes: true, qrCodes: true, placeholders: true },
  getStepCompletion: getAdmStepCompletion,
  lastActiveStep: admLastActiveStep,
};

registerChannelConfig(admChannelConfig);
